import {Component, isDevMode, NgZone, ViewChild} from '@angular/core';
import {ActivationStart, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router} from '@angular/router';
import {AlertController, IonSplitPane, MenuController, Platform} from '@ionic/angular';
import { SwUpdate } from '@angular/service-worker';
import { SplashScreen } from '@capacitor/splash-screen';
import {filter} from 'rxjs/operators';
import {SipRegisterProvider} from './providers/sip-register/sip-register';
import { Keyboard } from '@capacitor/keyboard';
import {PatientAlertProvider} from './providers/patient-alert/patient-alert';
import {FcmProvider} from './providers/fcm/fcm';
import {AndroidPermissions} from '@awesome-cordova-plugins/android-permissions/ngx';
import {SocketProvider} from './providers/socket/socket';
import {TranslateService} from '@ngx-translate/core';
import {User} from './providers/user/user';
import {Events} from './providers/events';
import {PageController} from './page-controller/page.controller';
import { ConfigurationService } from './providers/configuration.service';
import { firstValueFrom, Observable } from 'rxjs';
import { NetworkService } from './providers/network.service';
import { StatusBar, Style } from '@capacitor/status-bar';
import {BackgroundMode} from '@anuradev/capacitor-background-mode';

const setStatusBarStyleDefault = async () => {
  await StatusBar.setStyle({ style: Style.Default });
};

declare let cordova: any;
declare let Capacitor: any;

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss']
})
export class AppComponent {
  @ViewChild(IonSplitPane, {static: true}) ionSplitPane: IonSplitPane;

  menuExpand = false;
  private interval: any;
  configName:string;

  constructor(
    public pageController: PageController,
    public router: Router,
    public menu: MenuController,
    private platform: Platform,
    private translate: TranslateService,
    public user: User,
    private fcm: FcmProvider,
    private socketProvider: SocketProvider,
    private patientAlertProvider: PatientAlertProvider,
    private alertCtrl: AlertController,
    private events: Events,
    private androidPermissions: AndroidPermissions,
    private sipRegisterProvider: SipRegisterProvider,
    private configService: ConfigurationService,
    private networkService: NetworkService,
    private swUpdate: SwUpdate,
    private zone:NgZone,
  ) {

    if(!isDevMode()) {
      this.swUpdate.checkForUpdate().then(res => {
        if(res) {
          if (confirm("A new version of the app is available, do you want to update?")) {
            window.location.reload();
          }
        }
      });
    }

    this.translate.onLangChange.subscribe(() => {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      this.zone.run(()=>{});
    });
    
    this.events.subscribe('event:logout', () => {
      this.logout();
    });
    this.initializeApp();
  }

  async initializeApp() {
    await this.platform.ready();
    await this.initTranslate()
    this.initNavigation();
    await this.enableBackgroundMode();
    this.enableRtc();
    await this.requestPermission();

    this.networkService.addListener();
    this.networkService.initInterval();

    if (Capacitor.platform !== "web" && Capacitor.platform != "android") {
      await Keyboard.setAccessoryBarVisible({ isVisible: true });
    }

    if(Capacitor.platform === "capacitor") {
      setStatusBarStyleDefault();
    }

    this.configName = this.configService.getValue('configName').toString();

    let onlySpeechCommand = false;
    if (window.location.href.indexOf('speech-commant') !== -1) {
      onlySpeechCommand = true;
    }

    

    this.networkService.api.socket.connect();

    this.events.subscribe('socketConnect', async () => {

      if (this.user.api._user && this.user.api._user.token && !onlySpeechCommand) {        
        try {
          await this.networkService.api.test();
          const data:any = await firstValueFrom(this.user.api.checkLogin(this.user.api._user.token, this.user.api._user.password));
          if (data.data.data.profile) {
            Object.keys(data.data.data.profile).forEach((key) => {
              if (key === 'callNumber') {
                if (localStorage.getItem('hnr-callNumber')) {
                  data.data.data.profile[key] = localStorage.getItem('hnr-callNumber');
                }
              }

              this.user.api.updateHnrToken(key, data.data.data.profile[key]);
              this.user.api.socket.emit('user-connect', new Object()[this.user.api._user.user_id] = this.user.api.socket.ioSocket.id);
            });
          }
        } catch (_err) {
          console.log(_err);
        }

          this.platform.backButton.subscribe(() => {
            this.pageController.navigateRoot(this.pageController.page.Content);
          })


          localStorage.setItem('prom', this.user.api._user.user_id);
          await this.fcm.start();
          await this.fcm.getToken(this.user.api._user);


          let notify:Observable<any>;

          if (!this.platform.is('desktop')) {
            notify = this.fcm.listenToNotificationsBackground();
          } else {
            notify = this.fcm.onBackgroundMessageBrowser();
          }
            
          notify.subscribe(async (res: any) => {
              // App wurde aus dem hintergrund gestartet            
              if (res.topic.indexOf(this.user.api._user.provider) !== -1 && res.docId) {
                this.patientAlertProvider.getById(res.docId).subscribe((res2: any) => {
                  if (res2.data.data.data.isOpen === false) {
                    if (!this.socketProvider.openAlerts[res2.data.data.data.transmitterId]) {
                      this.socketProvider.presentConfirm(res2.data.data.data);
                    }
                  } else {
                    this.translate.get(['Error', 'ERRINPROGRESS', 'Close']).subscribe(async alert => {
                      const al = await this.alertCtrl.create({
                        header: alert.Error,
                        message: alert.ERRINPROGRESS + '<br>' + res2.data.data.data.editorPerson,
                        buttons: [alert.Close],
                        cssClass: 'alert-warning'
                      });
                      al.present();
                    });
                  }
                });
              }
            });

            SplashScreen.hide();
      } else {
        if (onlySpeechCommand) {
          this.pageController.navigateRoot(this.pageController.page.SpeechCommant);
        } else {
          this.pageController.navigateRoot(this.pageController.page.Login);
        }
        SplashScreen.hide();
      }

      this.networkService.api.initTimeOut = setTimeout(() => {
        this.networkService.api.switchConnect();
      }, 4500);
    });

  }

  private initNavigation() {
    const checkToastElements = (elements: any[]) => {
      if (elements.length > 0) {
        elements.forEach((e: any) => {
          if (e.classList.contains('auto-close')) {
            e.parentNode.removeChild(e);
          }
          if (e && e.parentNode) {
            return;
          }

        });
      }
    };

    const checkLoadingElements = (elements: any[]) => {
      if (elements.length > 0) {
        elements.forEach((e: any) => {
          if (e.classList.contains('no-auto-close')) {
            return;
          }
          if (e && e.parentNode) {
            e.parentNode.removeChild(e);
          }

        });
      }
    };

    this.router.events
      .pipe(filter(event => (
        event instanceof ActivationStart
        || event instanceof NavigationStart
        || event instanceof NavigationEnd
        || event instanceof NavigationCancel
        || event instanceof NavigationError
      )))
      .subscribe(async (event) => {
        // console.log('ROUTER', event);
        if (event instanceof NavigationStart) {
          this.menu.close();

        } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
          // console.log('NavigationEND', event);

          const toastElements = Array.from(document.querySelectorAll('ion-toast'));
          const loadingElements = Array.from(document.querySelectorAll('ion-loading'));
          checkToastElements(toastElements);
          checkLoadingElements(loadingElements);
        }
      });
  }

  expandMenu() {
    this.menuExpand = !this.menuExpand;
  }

  logout() {
    this.user.api.logout();
    this.pageController.navigateRoot(this.pageController.page.Login);
  }

  async initTranslate() {
    // Set the default language for translation strings, and the current language.

    this.translate.setDefaultLang('de');
    const browserLang = this.translate.getBrowserLang();
    const savedLanguage: string = localStorage.getItem('userlanguage');

    if (browserLang) {
      if (browserLang === 'zh') {
        const browserCultureLang = this.translate.getBrowserCultureLang();

        if (browserCultureLang.match(/-CN|CHS|Hans/i)) {
          this.translate.use('zh-cmn-Hans');
        } else if (browserCultureLang.match(/-TW|CHT|Hant/i)) {
          this.translate.use('zh-cmn-Hant');
        }
      } else {
        const lang = savedLanguage ? savedLanguage : this.translate.getBrowserLang();
        await firstValueFrom(this.translate.use(lang));
      }
    } else {
      await firstValueFrom(this.translate.use('de')); // Set your language here
    }

    this.translate.get(['BACK_BUTTON_TEXT']).subscribe(values => {
      //this.config.set('backButtonText', values.BACK_BUTTON_TEXT);
    });
  }

  openPage(page) {
    // Reset the content nav to have just this page
    // we wouldn't want the back button to show in this scenario
    this.pageController.navigateRoot(page.component);
  }

  private async enableBackgroundMode() {
    if(!this.patientAlertProvider.api.platform.is('desktop')) {
     
      await BackgroundMode.requestNotificationsPermission();
      await BackgroundMode.requestForegroundPermission();
      
      await BackgroundMode.requestDisableBatteryOptimizations();
      await BackgroundMode.setSettings({
          title: 'HITS-Notruf',
          text: 'HITS-Notruf läuft im Hintergrund',
          color: 'ff0000', // Hex format like 'ff0000'
          channelDescription: 'HITS-Notruf läuft im Hintergrund',
          resume: true,
          hidden: false,
          disableWebViewOptimization: true,
          silent: false
      })
      await BackgroundMode.enable();
    }
    
    if (this.patientAlertProvider.api.platform.is('android') && this.patientAlertProvider.api.platform.is('cordova')) {
      //BackgroundMode.overrideBackButton();

      BackgroundMode.addListener('appInBackground',
          async () => {
            await BackgroundMode.disableWebViewOptimizations();
            if (!this.interval) {
    
              const val = await BackgroundMode.isScreenOff();
              if (val) {
                console.log('++++++++Move to Background+++++++++');
                BackgroundMode.moveToBackground();
              }
                
    
              this.interval = setInterval(() => {
                console.log('#########interval register############');
                this.patientAlertProvider.api.socket.emit('Alive');
                this.sipRegisterProvider.socketAlive();
              }, 30000);
            }
          }
      );

      BackgroundMode.addListener('appInForeground', async () => {
        console.log('######background deactivate##########');
        await BackgroundMode.enableWebViewOptimizations();
        if (this.interval) {
          clearInterval(this.interval);
          this.interval = null;
        }
      });
    }
  }

  private enableRtc() {
    setTimeout(() => {
      if (this.patientAlertProvider.api.platform.is('ios') && this.patientAlertProvider.api.platform.is('cordova')) {
        cordova.plugins.iosrtc.registerGlobals();

        const adapterVersion = 'latest';
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = 'https://webrtc.github.io/adapter/adapter-' + adapterVersion + '.js';
        script.async = false;
        document.getElementsByTagName('head')[0].appendChild(script);
      }
    }, 1000);
  }

  private async requestPermission() {
    if (this.patientAlertProvider.api.platform.is('android') && this.patientAlertProvider.api.platform.is('cordova')) {

      await this.androidPermissions.requestPermissions([
        this.androidPermissions.PERMISSION.READ_PHONE_STATE,
        this.androidPermissions.PERMISSION.CAMERA,
        this.androidPermissions.PERMISSION.RECORD_AUDIO,
        this.androidPermissions.PERMISSION.MODIFY_AUDIO_SETTINGS,
        this.androidPermissions.PERMISSION.ACCESS_COARSE_LOCATION,
        this.androidPermissions.PERMISSION.ACCESS_FINE_LOCATION,
        this.androidPermissions.PERMISSION.ACCESS_LOCATION_EXTRA_COMMANDS,
        this.androidPermissions.PERMISSION.FOREGROUND_SERVICE,
        this.androidPermissions.PERMISSION.WAKE_LOCK,
        this.androidPermissions.PERMISSION.SYSTEM_ALERT_WINDOW,
        this.androidPermissions.PERMISSION.POST_NOTIFICATIONS,
        this.androidPermissions.PERMISSION.HIDE_OVERLAY_WINDOWS,
      ]);
    }
  }
}
